/**
 * 表格属性面板：行列设置；表格边框预设；单元格背景与文本；合并单元格。
 * 壳级外观（描边/填充/圆角等）由 AppearanceSettings 管理。
 */
import { useCallback, useMemo, type ChangeEvent } from 'react';
import { Button, Card, InputGroup, ListBox, Select } from '@heroui/react';
import { HexColorControl } from '@/components/canvas/shared/components/HexColorControl';
import { IconPlus, IconMinus } from '@tabler/icons-react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { ElementPatch, Paint, TableCell, TableElement } from '@/components/canvas/document/publicTypes';
import {
  AppearanceSettings,
  BoxSizeSettings,
  LayoutSettings,
  TextSettings,
  appearanceValueFromAppearance,
  appearanceValueToAppearancePatch,
  boxSizeSettingsValueFromElement,
  layoutSettingsValueFromElement,
  type AppearanceSettingsValue,
  type BoxSizeSettingsValue,
  type CanvasAlignAction,
  type LayoutSettingsValue,
  type TextSettingsPatch,
} from '@/components/canvas/shared/controls';
import { patchMergeCellRange } from '@/components/canvas/elements/table/utils/tableStructure';
import {
  boxSizeControlPatchToElementPatch,
  firstFill,
  firstStroke,
  layoutControlPatchToElementPatch,
  paintToCss,
  withFirstFill,
  withFirstStroke,
} from '@/components/canvas/elements/model';
import { getCell } from '@/components/canvas/elements/table/utils/tableUtilsIndex';
import {
  TABLE_MIN_ROW_HEIGHT,
  TABLE_MIN_COL_WIDTH,
  TABLE_BORDER_COLOR,
  TABLE_BORDER_WIDTH,
  TABLE_BORDER_STYLE,
  TABLE_CELL_PADDING_DEFAULT,
  TABLE_DEFAULT_TEXT_COLOR,
  TABLE_DEFAULT_VERTICAL_ALIGN,
} from '@/components/canvas/shared/constants/legacyConfig';

type BorderPreset = NonNullable<TableElement['data']['borderPreset']>;
const BORDER_PRESETS: { id: BorderPreset; label: string }[] = [
  { id: 'full', label: '完整边线' },
  { id: 'outerOnly', label: '仅外框' },
  { id: 'outerInnerH', label: '外框+横线' },
  { id: 'outerHInnerH', label: '仅横线' },
  { id: 'outerInnerV', label: '外框+竖线' },
  { id: 'outerVInnerV', label: '仅竖线' },
];

function BorderPresetIcon({ preset, selected }: { preset: BorderPreset; selected: boolean }) {
  const stroke = selected ? 'currentColor' : 'currentColor';
  const w = 28;
  const h = 20;
  const pad = 2;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const lines: React.ReactNode[] = [];
  if (preset === 'full') {
    lines.push(<rect key="outline" x={pad} y={pad} width={innerW} height={innerH} fill="none" stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="v1" x1={pad + innerW / 2} y1={pad} x2={pad + innerW / 2} y2={pad + innerH} stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="v2" x1={pad + innerW} y1={pad + innerH / 2} x2={pad} y2={pad + innerH / 2} stroke={stroke} strokeWidth={1} />);
  } else if (preset === 'outerOnly') {
    lines.push(<rect key="outline" x={pad} y={pad} width={innerW} height={innerH} fill="none" stroke={stroke} strokeWidth={1} />);
  } else if (preset === 'outerInnerH') {
    lines.push(<rect key="outline" x={pad} y={pad} width={innerW} height={innerH} fill="none" stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="h" x1={pad} y1={pad + innerH / 2} x2={pad + innerW} y2={pad + innerH / 2} stroke={stroke} strokeWidth={1} />);
  } else if (preset === 'outerHInnerH') {
    lines.push(<line key="t" x1={pad} y1={pad} x2={pad + innerW} y2={pad} stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="b" x1={pad} y1={pad + innerH} x2={pad + innerW} y2={pad + innerH} stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="h" x1={pad} y1={pad + innerH / 2} x2={pad + innerW} y2={pad + innerH / 2} stroke={stroke} strokeWidth={1} />);
  } else if (preset === 'outerInnerV') {
    lines.push(<rect key="outline" x={pad} y={pad} width={innerW} height={innerH} fill="none" stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="v" x1={pad + innerW / 2} y1={pad} x2={pad + innerW / 2} y2={pad + innerH} stroke={stroke} strokeWidth={1} />);
  } else {
    lines.push(<line key="l" x1={pad} y1={pad} x2={pad} y2={pad + innerH} stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="r" x1={pad + innerW} y1={pad} x2={pad + innerW} y2={pad + innerH} stroke={stroke} strokeWidth={1} />);
    lines.push(<line key="v" x1={pad + innerW / 2} y1={pad} x2={pad + innerW / 2} y2={pad + innerH} stroke={stroke} strokeWidth={1} />);
  }
  return (
    <svg width={w} height={h} className={selected ? 'text-primary-foreground' : 'text-muted'} aria-hidden>
      {lines}
    </svg>
  );
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export interface TablePropertiesProps {
  element: TableElement;
}

const TABLE_DEFAULT_FILL = '#666666';

type TableFillKind = 'table' | 'row' | 'col' | 'cell';
interface TableFillEntry {
  id: string;
  kind: TableFillKind;
  label: string;
  row?: number;
  col?: number;
  color: string;
}


function withCellFill(cell: TableCell | undefined, color: string): TableCell {
  return {
    ...cell,
    appearance: {
      opacity: cell?.appearance?.opacity ?? 1,
      ...cell?.appearance,
      fills: withFirstFill(color),
    },
  };
}

function withoutCellFill(cell: TableCell | undefined): TableCell | undefined {
  if (!cell) return undefined;
  const nextAppearance = cell.appearance
    ? { ...cell.appearance, fills: undefined }
    : undefined;
  if (!nextAppearance) return cell.text ? { ...cell, appearance: undefined } : undefined;
  const hasAppearance = Object.values(nextAppearance).some((value) => value !== undefined);
  const nextCell = {
    ...cell,
    appearance: hasAppearance ? nextAppearance : undefined,
  };
  if (!nextCell.text && !nextCell.textStyle && !nextCell.appearance) return undefined;
  return nextCell;
}

function mapTableTextSettingsPatch(patch: TextSettingsPatch): ElementPatch<'table'> {
  return {
    typography: {
      ...(patch.fontFamily !== undefined ? { fontFamily: patch.fontFamily } : {}),
      ...(patch.fontWeight !== undefined ? { fontWeight: patch.fontWeight } : {}),
      ...(patch.fontSize !== undefined ? { fontSize: patch.fontSize } : {}),
      ...(patch.color !== undefined ? { color: patch.color } : {}),
      ...(patch.lineHeight !== undefined ? { lineHeight: patch.lineHeight } : {}),
      ...(patch.letterSpacing !== undefined ? { letterSpacing: patch.letterSpacing } : {}),
    },
    paragraph: {
      ...(patch.textAlign !== undefined ? { textAlign: patch.textAlign } : {}),
      ...(patch.verticalAlign !== undefined ? { verticalAlign: patch.verticalAlign } : {}),
      ...('writingMode' in patch ? { writingMode: patch.writingMode } : {}),
    },
  };
}

export function TableProperties({ element: el }: TablePropertiesProps) {
  const {
    updateElement,
    getCurrentPage,
    selectedTableCells,
    selectedTableRow,
    selectedTableColumn,
    selectedElementIds,
    setSelectedTableCells,
    setSelectedTableRow,
    setSelectedTableColumn,
    selectElements,
  } = useCanvas();

  const effectiveRowHeight = useMemo(() => {
    if (el.data.rowHeights && el.data.rowHeights.length >= el.data.rows) {
      const sum = el.data.rowHeights.slice(0, el.data.rows).reduce((a, b) => a + b, 0);
      return Math.round(sum / el.data.rows);
    }
    return el.data.rows > 0 ? Math.round(el.size.height / el.data.rows) : TABLE_MIN_ROW_HEIGHT;
  }, [el.data.rows, el.size.height, el.data.rowHeights]);
  const effectiveColWidth = useMemo(() => {
    if (el.data.colWidths && el.data.colWidths.length >= el.data.cols) {
      const sum = el.data.colWidths.slice(0, el.data.cols).reduce((a, b) => a + b, 0);
      return Math.round(sum / el.data.cols);
    }
    return el.data.cols > 0 ? Math.round(el.size.width / el.data.cols) : TABLE_MIN_COL_WIDTH;
  }, [el.data.cols, el.size.width, el.data.colWidths]);

  const handleRowsChange = useCallback(
    (value: number) => {
      const rowsNew = Math.max(1, Math.min(100, Math.round(value)));
      const h = effectiveRowHeight;
      const rowHeightsNew = Array.from({ length: rowsNew }, () => h);
      updateElement(el.id, {
        data: { rows: rowsNew, rowHeights: rowHeightsNew },
        size: { height: rowsNew * h },
      });
    },
    [el.id, effectiveRowHeight, updateElement]
  );
  const handleColsChange = useCallback(
    (value: number) => {
      const colsNew = Math.max(1, Math.min(100, Math.round(value)));
      const w = effectiveColWidth;
      const colWidthsNew = Array.from({ length: colsNew }, () => w);
      updateElement(el.id, {
        data: { cols: colsNew, colWidths: colWidthsNew },
        size: { width: colsNew * w },
      });
    },
    [el.id, effectiveColWidth, updateElement]
  );
  const handleRowHeightChange = useCallback(
    (value: number) => {
      const h = Math.max(TABLE_MIN_ROW_HEIGHT, Math.round(value));
      const rowHeightsNew = Array.from({ length: el.data.rows }, () => h);
      updateElement(el.id, {
        data: { rowHeights: rowHeightsNew },
        size: { height: el.data.rows * h },
      });
    },
    [el.id, el.data.rows, updateElement]
  );
  const handleColWidthChange = useCallback(
    (value: number) => {
      const w = Math.max(TABLE_MIN_COL_WIDTH, Math.round(value));
      const colWidthsNew = Array.from({ length: el.data.cols }, () => w);
      updateElement(el.id, {
        data: { colWidths: colWidthsNew },
        size: { width: el.data.cols * w },
      });
    },
    [el.id, el.data.cols, updateElement]
  );

  const isCellSelection = selectedTableCells?.elementId === el.id;
  const isTableSelected =
    selectedElementIds?.includes(el.id) &&
    selectedTableCells?.elementId !== el.id &&
    selectedTableRow?.elementId !== el.id &&
    selectedTableColumn?.elementId !== el.id;
  const r0 = isCellSelection ? Math.min(selectedTableCells!.r0, selectedTableCells!.r1) : 0;
  const c0 = isCellSelection ? Math.min(selectedTableCells!.c0, selectedTableCells!.c1) : 0;
  const r1 = isCellSelection ? Math.max(selectedTableCells!.r0, selectedTableCells!.r1) : 0;
  const c1 = isCellSelection ? Math.max(selectedTableCells!.c0, selectedTableCells!.c1) : 0;
  const isSingleCell = isCellSelection && r0 === r1 && c0 === c1;
  const singleCell = isSingleCell ? getCell(el, r0, c0) : undefined;
  const mergedRanges = el.data.mergedRanges ?? [];
  const hasOverlap = mergedRanges.some(
    ([a0, a1, a2, a3]) => !(a2 < r0 || a0 > r1 || a3 < c0 || a1 > c1)
  );

  const backgroundEntries = useMemo((): TableFillEntry[] => {
    const list: TableFillEntry[] = [];
    const tableFill = paintToCss(firstFill(el.appearance));
    if (tableFill != null && tableFill !== '') {
      list.push({ id: 'table', kind: 'table', label: '全部', color: tableFill });
    }
    const rowColors = el.data.rowBackgrounds ?? {};
    Object.keys(rowColors)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((row) => {
        const color = paintToCss(rowColors[String(row)]?.[0]);
        if (color) list.push({ id: `row-${row}`, kind: 'row', label: `行${row + 1}`, row, color });
      });
    const colColors = el.data.colBackgrounds ?? {};
    Object.keys(colColors)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((col) => {
        const color = paintToCss(colColors[String(col)]?.[0]);
        if (color) list.push({ id: `col-${col}`, kind: 'col', label: `列${col + 1}`, col, color });
      });
    const cells = el.data.cells ?? {};
    Object.entries(cells).forEach(([key, cell]) => {
      const bg = paintToCss(cell?.appearance?.fills?.[0]);
      if (!bg) return;
      const [r, c] = key.split(',').map(Number);
      list.push({
        id: `cell-${r}-${c}`,
        kind: 'cell',
        label: `${r + 1},${c + 1}`,
        row: r,
        col: c,
        color: bg,
      });
    });
    list.sort((a, b) => {
      const order = { table: 0, row: 1, col: 2, cell: 3 };
      if (order[a.kind] !== order[b.kind]) return order[a.kind] - order[b.kind];
      if (a.kind === 'row' && b.kind === 'row') return (a.row ?? 0) - (b.row ?? 0);
      if (a.kind === 'col' && b.kind === 'col') return (a.col ?? 0) - (b.col ?? 0);
      if (a.kind === 'cell' && b.kind === 'cell')
        return (a.row ?? 0) - (b.row ?? 0) || (a.col ?? 0) - (b.col ?? 0);
      return 0;
    });
    return list;
  }, [el.appearance, el.data.rowBackgrounds, el.data.colBackgrounds, el.data.cells]);

  const canAddFill =
    isTableSelected ||
    selectedTableRow?.elementId === el.id ||
    selectedTableColumn?.elementId === el.id ||
    isCellSelection;

  const addFill = useCallback(() => {
    if (isTableSelected) {
      updateElement(el.id, { appearance: { fills: withFirstFill(TABLE_DEFAULT_FILL) } });
      return;
    }
    if (selectedTableRow?.elementId === el.id) {
      const row = selectedTableRow.row;
      const next: Record<string, Paint[]> = { ...(el.data.rowBackgrounds ?? {}), [String(row)]: withFirstFill(TABLE_DEFAULT_FILL) ?? [] };
      updateElement(el.id, { data: { rowBackgrounds: next } });
      return;
    }
    if (selectedTableColumn?.elementId === el.id) {
      const col = selectedTableColumn.col;
      const next: Record<string, Paint[]> = { ...(el.data.colBackgrounds ?? {}), [String(col)]: withFirstFill(TABLE_DEFAULT_FILL) ?? [] };
      updateElement(el.id, { data: { colBackgrounds: next } });
      return;
    }
    if (isCellSelection) {
      const cells = { ...(el.data.cells ?? {}) };
      for (let ri = r0; ri <= r1; ri++) {
        for (let ci = c0; ci <= c1; ci++) {
          const key = `${ri},${ci}`;
          cells[key] = withCellFill(cells[key], TABLE_DEFAULT_FILL);
        }
      }
      updateElement(el.id, { data: { cells } });
    }
  }, [
    el.id,
    el.data.cells,
    el.data.rowBackgrounds,
    el.data.colBackgrounds,
    isTableSelected,
    selectedTableRow,
    selectedTableColumn,
    isCellSelection,
    r0,
    c0,
    r1,
    c1,
    updateElement,
  ]);

  const removeFill = useCallback(
    (entry: TableFillEntry) => {
      if (entry.kind === 'table') {
        updateElement(el.id, { appearance: { fills: undefined } });
        return;
      }
      if (entry.kind === 'row' && entry.row !== undefined) {
        const next: Record<string, Paint[]> = { ...(el.data.rowBackgrounds ?? {}) };
        delete next[String(entry.row)];
        updateElement(el.id, { data: { rowBackgrounds: next } });
        return;
      }
      if (entry.kind === 'col' && entry.col !== undefined) {
        const next: Record<string, Paint[]> = { ...(el.data.colBackgrounds ?? {}) };
        delete next[String(entry.col)];
        updateElement(el.id, { data: { colBackgrounds: next } });
        return;
      }
      if (entry.kind === 'cell' && entry.row !== undefined && entry.col !== undefined) {
        const cells = { ...(el.data.cells ?? {}) };
        const key = `${entry.row},${entry.col}`;
        const nextCell = withoutCellFill(cells[key]);
        if (nextCell) cells[key] = nextCell;
        else delete cells[key];
        updateElement(el.id, { data: { cells } });
      }
    },
    [el, updateElement]
  );

  const selectRegion = useCallback(
    (entry: TableFillEntry) => {
      selectElements([el.id]);
      setSelectedTableRow(null);
      setSelectedTableColumn(null);
      setSelectedTableCells(null);
      if (entry.kind === 'table') return;
      if (entry.kind === 'row' && entry.row !== undefined) {
        setSelectedTableRow({ elementId: el.id, row: entry.row });
        return;
      }
      if (entry.kind === 'col' && entry.col !== undefined) {
        setSelectedTableColumn({ elementId: el.id, col: entry.col });
        return;
      }
      if (entry.kind === 'cell' && entry.row !== undefined && entry.col !== undefined) {
        setSelectedTableCells({
          elementId: el.id,
          r0: entry.row,
          c0: entry.col,
          r1: entry.row,
          c1: entry.col,
        });
      }
    },
    [el.id, selectElements, setSelectedTableRow, setSelectedTableColumn, setSelectedTableCells]
  );

  const updateFillColor = useCallback(
    (entry: TableFillEntry, value: string) => {
      if (value.startsWith('linear-gradient') || value.startsWith('radial-gradient')) return;
      if (entry.kind === 'table') {
        updateElement(el.id, { appearance: { fills: withFirstFill(value) } });
        return;
      }
      if (entry.kind === 'row' && entry.row !== undefined) {
        const next: Record<string, Paint[]> = { ...(el.data.rowBackgrounds ?? {}), [String(entry.row)]: withFirstFill(value) ?? [] };
        updateElement(el.id, { data: { rowBackgrounds: next } });
        return;
      }
      if (entry.kind === 'col' && entry.col !== undefined) {
        const next: Record<string, Paint[]> = { ...(el.data.colBackgrounds ?? {}), [String(entry.col)]: withFirstFill(value) ?? [] };
        updateElement(el.id, { data: { colBackgrounds: next } });
        return;
      }
      if (entry.kind === 'cell' && entry.row !== undefined && entry.col !== undefined) {
        const cells = { ...(el.data.cells ?? {}) };
        const key = `${entry.row},${entry.col}`;
        cells[key] = withCellFill(cells[key], value);
        updateElement(el.id, { data: { cells } });
      }
    },
    [el.id, el.data.cells, el.data.rowBackgrounds, el.data.colBackgrounds, updateElement]
  );

  const handleMerge = useCallback(() => {
    const patch = patchMergeCellRange(el, r0, c0, r1, c1);
    if (patch) updateElement(el.id, patch);
  }, [el, r0, c0, r1, c1, updateElement]);

  const handleUnmerge = useCallback(() => {
    const next = mergedRanges.filter(
      ([a0, a1, a2, a3]) => a2 < r0 || a0 > r1 || a3 < c0 || a1 > c1
    );
    updateElement(el.id, { data: { mergedRanges: next } });
  }, [el.id, mergedRanges, r0, c0, r1, c1, updateElement]);

  const appearanceValue = appearanceValueFromAppearance(el.appearance);

  const handleLayoutChange = useCallback(
    (patch: Partial<LayoutSettingsValue>) => {
      updateElement(el.id, layoutControlPatchToElementPatch(el, patch));
    },
    [el, updateElement],
  );

  const handleBoxSizeChange = useCallback(
    (patch: Partial<BoxSizeSettingsValue>) => {
      updateElement(el.id, boxSizeControlPatchToElementPatch(patch));
    },
    [el.id, updateElement],
  );

  const handleAppearanceChange = useCallback(
    (patch: Partial<AppearanceSettingsValue>) => {
      const next = { ...appearanceValue, ...patch };
      updateElement(el.id, {
        appearance: { ...el.appearance, ...appearanceValueToAppearancePatch(next, el.appearance) },
      });
    },
    [appearanceValue, el.appearance, el.id, updateElement],
  );

  const handleAlignTableToCanvas = useCallback(
    (action: CanvasAlignAction) => {
      const page = getCurrentPage();
      if (!page) return;
      const { width: cw, height: ch } = page.size;
      const w = el.size.width;
      const h = el.size.height;
      let x = el.layout.x;
      let y = el.layout.y;
      if (action === 'left') x = 0;
      if (action === 'right') x = cw - w;
      if (action === 'top') y = 0;
      if (action === 'bottom') y = ch - h;
      if (action === 'center') {
        x = (cw - w) / 2;
        y = (ch - h) / 2;
      }
      updateElement(el.id, { layout: { x, y } });
    },
    [getCurrentPage, el.id, el.size.width, el.size.height, el.layout.x, el.layout.y, updateElement]
  );

  const currentPreset: BorderPreset = el.data.borderPreset ?? 'full';
  const stroke = firstStroke(el.appearance);

  return (
    <div className="property-panel-view min-h-0 w-full shrink-0 space-y-4">
      <BoxSizeSettings value={boxSizeSettingsValueFromElement(el)} onChange={handleBoxSizeChange} />
      <LayoutSettings
        value={layoutSettingsValueFromElement(el)}
        onChange={handleLayoutChange}
        onAlignToCanvas={handleAlignTableToCanvas}
      />
      <AppearanceSettings value={appearanceValue} onChange={handleAppearanceChange} />

      <div className="space-y-2 w-full">
        <p className="text-sm font-medium">行列设置</p>
        <div className="flex flex-col gap-3 w-full">
          <div className="w-full border border-divider rounded-lg overflow-hidden">
            <InputGroup className="rounded-none border-0 border-b border-divider shadow-none">
              <InputGroup.Prefix className="text-muted font-normal">
                <span className="font-normal">行数</span>
              </InputGroup.Prefix>
              <InputGroup.Input
                aria-label="表格行数"
                type="number"
                min={1}
                max={100}
                value={el.data.rows}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleRowsChange(num(e.target.value))}
                className="text-center"
              />
              <InputGroup.Suffix className="font-normal">
                <span className="font-normal">行</span>
              </InputGroup.Suffix>
            </InputGroup>
            <InputGroup className="rounded-none border-0 shadow-none">
              <InputGroup.Prefix className="text-muted font-normal">
                <span className="font-normal">列数</span>
              </InputGroup.Prefix>
              <InputGroup.Input
                aria-label="表格列数"
                type="number"
                min={1}
                max={100}
                value={el.data.cols}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleColsChange(num(e.target.value))}
                className="text-center"
              />
              <InputGroup.Suffix className="font-normal">
                <span className="font-normal">列</span>
              </InputGroup.Suffix>
            </InputGroup>
          </div>
          <div className="w-full border border-divider rounded-lg overflow-hidden">
            <InputGroup className="rounded-none border-0 border-b border-divider shadow-none">
              <InputGroup.Prefix className="text-muted font-normal">
                <span className="font-normal">行高</span>
              </InputGroup.Prefix>
              <InputGroup.Input
                aria-label="表格行高（像素）"
                type="number"
                min={TABLE_MIN_ROW_HEIGHT}
                value={effectiveRowHeight}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleRowHeightChange(num(e.target.value))}
                className="text-center"
              />
              <InputGroup.Suffix className="font-normal">
                <span className="font-normal">px</span>
              </InputGroup.Suffix>
            </InputGroup>
            <InputGroup className="rounded-none border-0 shadow-none">
              <InputGroup.Prefix className="text-muted font-normal">
                <span className="font-normal">列宽</span>
              </InputGroup.Prefix>
              <InputGroup.Input
                aria-label="表格列宽（像素）"
                type="number"
                min={TABLE_MIN_COL_WIDTH}
                value={effectiveColWidth}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleColWidthChange(num(e.target.value))}
                className="text-center"
              />
              <InputGroup.Suffix className="font-normal">
                <span className="font-normal">px</span>
              </InputGroup.Suffix>
            </InputGroup>
          </div>
        </div>
      </div>

      <div className="space-y-2 w-full">
        <p className="text-sm font-medium">表格边框</p>
        <div className="grid grid-cols-3 gap-1.5">
          {BORDER_PRESETS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`flex flex-col items-center gap-1 rounded-md border p-2 text-xs transition-colors ${
                currentPreset === id
                  ? 'bg-primary border-primary text-primary-foreground [&_svg]:text-primary-foreground'
                  : 'border-divider bg-surface hover:bg-default-200/80'
              }`}
              onClick={() =>
                updateElement(el.id, {
                  data: { borderPreset: id },
                  appearance: {
                    strokes: withFirstStroke(stroke, {
                      ...(stroke?.width === undefined || stroke.width === 0 ? { width: TABLE_BORDER_WIDTH } : {}),
                      ...(stroke?.color === undefined ? { color: TABLE_BORDER_COLOR } : {}),
                      ...(stroke?.style === undefined ? { style: TABLE_BORDER_STYLE } : {}),
                    }, { color: TABLE_BORDER_COLOR, width: TABLE_BORDER_WIDTH, style: TABLE_BORDER_STYLE }),
                  },
                })
              }
            >
              <BorderPresetIcon preset={id} selected={currentPreset === id} />
              <span className="font-normal">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2 w-full">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-default-foreground">背景色</p>
          <Button
            variant="outline"
            isIconOnly
            size="sm"
            onPress={addFill}
            isDisabled={!canAddFill}
            aria-label={
              canAddFill ? '为当前选中区域添加背景色' : '请先在画布中选中表格、行、列或单元格'
            }
          >
            <IconPlus className="h-4 w-4" stroke={1.5} />
          </Button>
        </div>
        <ul className="space-y-2">
          {backgroundEntries.map((entry) => (
            <li key={entry.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => selectRegion(entry)}
                className="min-w-[3rem] shrink-0 text-left text-xs font-normal text-muted transition-colors hover:text-default-foreground"
              >
                {entry.label}
              </button>
              <div className="flex-1 min-w-0">
                <HexColorControl
                  value={entry.color}
                  onChange={(value) => updateFillColor(entry, value)}
                  placeholder="选择或输入颜色"
                />
              </div>
              <Button
                variant="ghost"
                isIconOnly
                size="sm"
                onPress={() => removeFill(entry)}
                aria-label="移除该背景色"
              >
                <IconMinus className="h-4 w-4" stroke={1.5} />
              </Button>
            </li>
          ))}
        </ul>
      </div>

      <TextSettings
        value={{
          fontFamily: el.typography.fontFamily,
          fontWeight: el.typography.fontWeight,
          fontSize: el.typography.fontSize,
          lineHeight: el.typography.lineHeight,
          letterSpacing: el.typography.letterSpacing,
          textColor: el.typography.color ?? TABLE_DEFAULT_TEXT_COLOR,
          textAlign: el.paragraph.textAlign,
          verticalAlign: el.paragraph.verticalAlign ?? TABLE_DEFAULT_VERTICAL_ALIGN,
          writingMode: el.paragraph.writingMode ?? 'horizontal-tb',
        }}
        onChange={(patch) =>
          updateElement(el.id, mapTableTextSettingsPatch(patch as TextSettingsPatch))
        }
      />

      {isCellSelection && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onPress={handleMerge} isDisabled={isSingleCell}>
            合并单元格
          </Button>
          <Button variant="outline" size="sm" onPress={handleUnmerge} isDisabled={!hasOverlap}>
            取消合并
          </Button>
        </div>
      )}
    </div>
  );
}
